// Home: Proton's post-login "Welcome" page
// (applications/account/src/app/public/AppSwitcherContainer.tsx +
//  components/ExploreAppsListV2). Centered welcome, a grid of app tiles, a
// Settings pill at the bottom. The overview lives in Proton's own idioms
// instead of dashboard boxes: live counts ride the tile badge Proton uses for
// "New", each tile's description line carries that app's live status, and the
// next meeting today sits under the heading as a pill.
import { useMemo, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Mail, Calendar, FolderClosed, Users, Video, LifeBuoy, Settings as Cog } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useMe, useMailSession, useMailboxes, useTickets, useEvents } from "@/hooks";
import { formatTimeInTz, browserTz } from "@/util/tz";
import { Icon } from "@/proton/ui/Icon";

interface Tile {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: string | null;
  status?: string | null;
  statusTone?: "danger" | "warning";
}

export function HomePage() {
  const me = useMe();
  const isAgent = (me.data?.groups ?? []).includes("support-agents");
  const first = (me.data?.name ?? "").split(" ")[0];
  const userTz = me.data?.timezone || browserTz();

  const mail = useMailOverview();
  const today = useTodayOverview(userTz);

  const baseTiles: Tile[] = [
    { to: "/mail", label: "Mail", icon: Mail,
      badge: mail.unread ? String(mail.unread) : null,
      status: mail.unread == null ? null : mail.unread ? `${mail.unread} unread` : "Up to date" },
    { to: "/calendar", label: "Calendar", icon: Calendar,
      badge: today.remaining ? String(today.remaining) : null,
      status: today.next ? `Next ${today.next.time}` : today.loaded ? "Nothing else today" : null },
    { to: "/files", label: "Files", icon: FolderClosed, status: "Documents" },
    { to: "/contacts", label: "Contacts", icon: Users, status: "Address book" },
    { to: "/meetings", label: "Meet", icon: Video,
      status: today.nextMeeting ? `${today.nextMeeting.time} call` : "Start a meeting" },
  ];

  const dateLine = useMemo(
    () => new Intl.DateTimeFormat(undefined, { timeZone: userTz, weekday: "long", month: "long", day: "numeric" }).format(new Date()),
    [userTz],
  );

  return (
    <>
      <GlyphGradient />
      <div className="shrink-0">
        <header className="mt-6 mb-8 md:mb-10 lg:mb-20 text-center fade-in px-4">
          <h1 className="text-2xl md:text-6xl text-semibold mb-2">{first ? `Welcome, ${first}` : "Welcome"}</h1>
          <p className="m-0 md:text-lg color-weak">{dateLine}</p>
          {today.next && (
            <div className="mt-5 flex justify-center">
              <Link
                to={today.next.roomId ? `/meetings/${today.next.roomId}` : "/calendar"}
                className="button button-outline-weak button-small button-pill welcome-settings-pill inline-flex items-center gap-2 max-w-full"
                title={today.next.title}
              >
                <span className="color-weak shrink-0">Up next</span>
                <span className="text-ellipsis">{today.next.title}</span>
                <span className="color-weak shrink-0 text-tabular">{today.next.time}</span>
                {today.next.roomId && <span className="color-primary shrink-0">Join</span>}
              </Link>
            </div>
          )}
        </header>
        <nav className="flex gap-2 justify-center px-4 pb-4" aria-label="Apps">
          <TileGrid tiles={baseTiles} withSupport={isAgent} />
        </nav>
      </div>
      <div className="w-full text-center mt-auto fade-in py-4 shrink-0">
        <Link to="/settings" className="button button-outline-weak button-medium button-pill welcome-settings-pill inline-flex items-center gap-2">
          <Icon as={Cog} /> Settings
        </Link>
      </div>
    </>
  );
}

function TileGrid({ tiles, withSupport }: { tiles: Tile[]; withSupport: boolean }) {
  // Proton picks the column count from the app count (getGridColsClass):
  // 6 apps sit in two rows of three, 5 in a single row.
  const count = tiles.length + (withSupport ? 1 : 0);
  const cols = count === 6 ? "grid-cols-3" : "grid-cols-3 md:grid-cols-5";
  return (
    <ul className={clsx("explore-apps-list grid gap-4 md:gap-6 lg:gap-8", cols)}>
      {tiles.map((t, i) => <TileItem key={t.to} tile={t} index={i} />)}
      {withSupport && <SupportTile index={tiles.length} />}
    </ul>
  );
}

function TileItem({ tile, index }: { tile: Tile; index: number }) {
  return (
    <li className="explore-apps-list-item" style={{ "--animation-order": index } as CSSProperties}>
      <Link to={tile.to} className="explore-app" aria-label={tile.status ? `${tile.label}, ${tile.status}` : tile.label}>
        <div className="relative">
          <div className="explore-app-icon">
            <Icon as={tile.icon} size={9} strokeWidth={1.75} color="url(#l7-glyph)" />
          </div>
          {tile.badge && <span className="explore-app-new-badge">{tile.badge}</span>}
        </div>
        <div className="explore-app-name">{tile.label}</div>
        <div className={clsx("explore-app-status text-ellipsis max-w-full", tile.statusTone && `color-${tile.statusTone}`)}>
          {tile.status ?? " "}
        </div>
      </Link>
    </li>
  );
}

/** Separate component so the tickets query only runs for support agents. */
function SupportTile({ index }: { index: number }) {
  const q = useTickets({ status: "open" });
  const items = q.data?.items ?? [];
  const urgent = items.filter((t) => t.priority === "urgent").length;
  const high = items.filter((t) => t.priority === "high").length;
  return (
    <TileItem
      index={index}
      tile={{
        to: "/support", label: "Support", icon: LifeBuoy,
        badge: items.length ? String(items.length) : null,
        status: q.isLoading ? null : urgent ? `${urgent} urgent` : high ? `${high} high priority` : items.length ? `${items.length} open` : "All clear",
        statusTone: urgent ? "danger" : high ? "warning" : undefined,
      }}
    />
  );
}

/** Stroke gradient for the tile glyphs: Proton's glyphs are two-tone; ours
 *  run the Layer7 blue ramp (primary → interaction-norm-minor). userSpaceOnUse
 *  so straight strokes (zero-height bounding boxes) still paint. */
function GlyphGradient() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="l7-glyph" gradientUnits="userSpaceOnUse" x1="2" y1="2" x2="22" y2="22">
          <stop offset="0%" stopColor="#64b9fe" />
          <stop offset="100%" stopColor="#1668b8" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* ── Live data ──────────────────────────────────────────────────────── */

function useMailOverview(): { unread: number | null } {
  const session = useMailSession();
  const mailboxesQ = useMailboxes(session.data?.accountId);
  const inbox = mailboxesQ.data?.find((m) => m.role === "inbox");
  return { unread: inbox ? inbox.unread_emails : null };
}

function useTodayOverview(userTz: string) {
  // Day window in the user's saved timezone (not browser-local), using the
  // same two-pass offset solve as inputLocalToTsInTz in util/tz.
  const { dayStart, dayEnd } = useMemo(() => {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: userTz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "0";
    const utcGuess = Date.UTC(+get("year"), +get("month") - 1, +get("day"), 0, 0);
    const wall = new Intl.DateTimeFormat("sv-SE", { timeZone: userTz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })
      .format(new Date(utcGuess)).replace(" ", "T");
    const m = wall.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    const wallUtc = m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : utcGuess;
    const start = utcGuess - (wallUtc - utcGuess);
    return { dayStart: start, dayEnd: start + 86_400_000 };
  }, [userTz]);
  const q = useEvents({ start_ts: dayStart, end_ts: dayEnd });
  const upcoming = (q.data ?? []).filter((e) => e.end_ts >= Date.now()).sort((a, b) => a.start_ts - b.start_ts);
  const fmt = (e: (typeof upcoming)[number]) => ({ title: e.title, time: formatTimeInTz(e.start_ts, userTz), roomId: e.meeting_room_id ?? null });
  const next = upcoming[0] ? fmt(upcoming[0]) : null;
  const meeting = upcoming.find((e) => e.meeting_room_id);
  return {
    loaded: q.isSuccess,
    remaining: q.isSuccess ? upcoming.length : null,
    next,
    nextMeeting: meeting ? fmt(meeting) : null,
  };
}
